import { createSlice } from "@reduxjs/toolkit";

import { clearStore } from "actions/initialization";
import {
  FOLDERS_SORT_KEYS,
  initialFolderListPaginationParams,
  LOCALSTORAGE_ITEMS,
  PAGINATION,
  SORT_DIRECTION,
} from "../constants";
import {
  foldersListThunk,
  createFolderThunk,
  updateFolderThunk,
  deleteFolderThunk,
  statsFolderThunk,
  searchFoldersThunk,
  exportFolderThunk,
} from "thunks/folder";
import localStorageService from "services/localStorage";

const initialState = {
  ...initialFolderListPaginationParams,
  ids: [],
  hash: {},
  search: null,
  sort: {
    key: Object.keys(FOLDERS_SORT_KEYS)
      .map((k) => FOLDERS_SORT_KEYS[k].key)
      .includes(
        localStorageService.getItem(LOCALSTORAGE_ITEMS.sort.folders.key)
      )
      ? localStorageService.getItem(LOCALSTORAGE_ITEMS.sort.folders.key)
      : FOLDERS_SORT_KEYS.UPDATED_AT_DESC.key,
    direction: Object.keys(SORT_DIRECTION)
      .map((k) => SORT_DIRECTION[k])
      .includes(
        localStorageService.getItem(LOCALSTORAGE_ITEMS.sort.folders.direction)
      )
      ? localStorageService.getItem(LOCALSTORAGE_ITEMS.sort.folders.direction)
      : SORT_DIRECTION.desc,
  },
  loading: {
    create: null,
    update: null,
    delete: null,
    stats: null,
    export: {},
  },
  cachedAt: {},
  error: {
    create: null,
    update: null,
    delete: null,
    stats: null,
    search: null,
    export: null,
  },
  selectedId: null,
  currentProjectId: null,
};

const folderSlice = createSlice({
  name: "folder",
  initialState,
  reducers: {
    setSelectedFolder(state, action) {
      const folderId = action.payload;
      state.selectedId = folderId;
    },
    projectStatsUpdateFolder(state, action) {
      const { folderId, stats } = action.payload;
      if (folderId && state.hash[folderId]) {
        state.hash[folderId].stats = stats;
      }
    },
    folderListSetPaginationData(state, action) {
      const { pagination, sort } = action.payload;
      if (pagination) {
        const { count, currentPage, limit } = pagination;
        if (currentPage) {
          state.pagination.currentPage = currentPage;
          state.pagination.offset = (currentPage - 1) * state.pagination.limit;
        }
        if (count) {
          state.pagination.count = count;
        }
        if (limit) {
          state.pagination.limit = limit;
        }
        state.pagination.forceFetch = true;
      }
      if (sort) {
        state.hash = {};
        state.ids = [];
        state.pagination = { ...initialFolderListPaginationParams.pagination };
        state.sort = sort;
      }
    },
    folderCleanUp(state, action) {
      const folderId = action.payload;
      const selectedIdIndex = state.ids.indexOf(state.selectedId);

      state.ids = state.ids.filter((id) => id !== folderId);
      if (state.hash[folderId]) {
        delete state.hash[folderId];
      }

      if (folderId === state.selectedId) {
        if (state.ids.length === 0) {
          state.selectedId = null;
        }

        state.selectedId = state.ids[selectedIdIndex]
          ? state.ids[selectedIdIndex]
          : state.ids[selectedIdIndex - 1];
      }

      if (
        state.pagination.currentPage === state.pagination.totalPages &&
        state.pagination.currentPage > 1 &&
        state.ids.length % state.pagination.limit
      ) {
        state.pagination.currentPage = state.pagination.currentPage - 1;
        state.pagination.offset =
          (state.pagination.currentPage - 1) * state.pagination.limit;
      }
      if (
        state.pagination.currentPage !== state.pagination.totalPages &&
        state.ids.length % state.pagination.limit < state.pagination.limit
      ) {
        state.pagination.forceFetch = true;
      }
      if (
        state.pagination.currentPage === 1 &&
        state.ids.length !== state.pagination.count
      ) {
        state.pagination.count = state.ids.length;
      }
    },
    setSearchPrompt(state, action) {
      state.search = action.payload;
    },
    resetFolders(state) {
      state.hash = {};
      state.ids = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // #region pending
      .addCase(foldersListThunk.pending, (state, action) => {
        const { projectId } = action.meta.arg;
        state.loading[projectId] = true;
      })
      .addCase(createFolderThunk.pending, (state) => {
        state.loading.create = true;
      })
      .addCase(updateFolderThunk.pending, (state) => {
        state.loading.update = true;
      })
      .addCase(deleteFolderThunk.pending, (state) => {
        state.loading.delete = true;
      })
      .addCase(statsFolderThunk.pending, (state) => {
        state.loading.stats = true;
      })
      .addCase(searchFoldersThunk.pending, (state, action) => {
        const { projectId } = action.meta.arg;
        state.loading[projectId] = true;
      })
      .addCase(exportFolderThunk.pending, (state, action) => {
        const { folderId } = action.meta.arg;
        state.loading.export[folderId] = true;
      })
      // #endregion
      // #region fulfilled
      .addCase(foldersListThunk.fulfilled, (state, action) => {
        const {
          meta: {
            arg: { projectId },
          },
          payload: folders,
        } = action;
        state.loading[projectId] = false;
        const { list, pagination } = folders;
        const ids = list.map((folder) => folder.id);
        state.search = null;
        const tmpRecordings = {};
        if (
          state.currentProjectId !== projectId ||
          state.pagination.currentPage === 1
        ) {
          state.ids = [];
        }
        ids.forEach((id) => {
          if (state.hash[id]) {
            tmpRecordings[id] = state.hash[id].recordings;
            delete state.hash[id];
          }
          if (!state.ids.includes(id)) {
            state.ids.push(id);
          }
        });
        list.forEach((folder) => {
          state.hash[folder.id] = {
            id: folder.id,
            projectId: folder.project_id,
            name: folder.name,
            active: folder.active,
            recordings: tmpRecordings[folder.id] || [],
            parentId: folder.parent_id,
          };
        });
        const { limit, ...otherPaginationData } = pagination;
        state.pagination = {
          ...state.pagination,
          ...otherPaginationData,
          totalPages: Math.ceil(otherPaginationData.count / limit) || 1,
          forceFetch: false,
        };
        state.cachedAt[projectId] = Date.now();
        if (ids.length > 0 && !ids.includes(state.selectedId)) {
          if (pagination.offset === 0) {
            state.selectedId = ids[0];
          } else {
            const selectedIdIndex = state.ids.indexOf(state.selectedId);
            state.selectedId = state.ids[selectedIdIndex];
          }
        }
        state.currentProjectId = projectId;
      })
      .addCase(createFolderThunk.fulfilled, (state, action) => {
        state.loading.create = false;
        const folder = action.payload;
        state.ids.unshift(folder.id);
        state.hash[folder.id] = {
          id: folder.id,
          projectId: folder.project_id,
          name: folder.name,
          active: folder.active,
          recordings: [],
          parentId: folder.parent_id,
        };
        if (state.ids.length > PAGINATION.folders) {
          state.ids.pop();
        }
        state.selectedId = folder.id;
        state.pagination = {
          ...state.pagination,
          count: state.pagination.count + 1,
          offset: 0,
          currentPage: 1,
        };
        if (state.pagination.count > state.pagination.limit) {
          state.pagination.totalPages =
            Math.ceil(state.pagination.count / state.pagination.limit) || 1;
        }
      })
      .addCase(updateFolderThunk.fulfilled, (state, action) => {
        const { id, name } = action.payload;
        state.loading.update = false;
        state.hash[id].name = name;
      })
      .addCase(deleteFolderThunk.fulfilled, (state) => {
        state.loading.delete = false;
      })
      .addCase(statsFolderThunk.fulfilled, (state, action) => {
        const { initialForderIds, folders } = action.payload;
        state.loading.stats = false;
        initialForderIds.forEach((id) => {
          if (state.hash[id]) {
            state.hash[id].stats = {};
          }
        });
        folders.forEach((folder) => {
          state.hash[folder.folder_id].stats = folder.stats;
        });
      })
      .addCase(searchFoldersThunk.fulfilled, (state, action) => {
        const {
          meta: {
            arg: { projectId },
          },
          payload: list,
        } = action;
        state.loading[projectId] = false;
        const ids = list.map((folder) => folder.id);
        const tmpRecordings = {};
        ids.forEach((id) => {
          if (state.hash[id]) {
            tmpRecordings[id] = state.hash[id].recordings;
            delete state.hash[id];
          }
          if (!state.ids.includes(id)) {
            state.ids.push(id);
          }
        });
        list.forEach((folder) => {
          state.hash[folder.id] = {
            id: folder.id,
            projectId: folder.project_id,
            name: folder.name,
            active: folder.active,
            recordings: tmpRecordings[folder.id] || [],
            parentId: folder.parent_id,
          };
        });
      })
      .addCase(exportFolderThunk.fulfilled, (state, action) => {
        const { folderId } = action.meta.arg;
        state.loading.export[folderId] = false;
      })
      // #endregion
      // #region rejected
      .addCase(foldersListThunk.rejected, (state, action) => {
        const {
          meta: {
            arg: { projectId },
          },
          payload: error,
        } = action;
        state.loading[projectId] = false;
        state.error[projectId] = error;
      })
      .addCase(createFolderThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.create = false;
        state.error.create = error;
      })
      .addCase(updateFolderThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.update = false;
        state.error.update = error;
      })
      .addCase(deleteFolderThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.delete = false;
        state.error.delete = error;
      })
      .addCase(statsFolderThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.stats = false;
        state.error.stats = error;
      })
      .addCase(searchFoldersThunk.rejected, (state, action) => {
        const {
          meta: {
            arg: { projectId },
          },
          payload: error,
        } = action;
        state.loading[projectId] = false;
        state.error.search = error;
      })
      .addCase(exportFolderThunk.rejected, (state, action) => {
        const {
          meta: {
            arg: { folderId },
          },
          error,
        } = action;
        state.loading.export[folderId] = false;
        state.error.export = error;
      })
      // #endregion
      .addCase(clearStore, () => initialState);
  },
});

export const actions = folderSlice.actions;

export default folderSlice.reducer;
